/**
 * The one HTTP client for backend calls. `VITE_API_URL` defaults to the same
 * origin, where vite and nginx proxy /api; paths carry their /api/v1 prefix.
 */

import { getApiBase } from "./chainSelect";
import { getStoredToken } from "./auth";
import { errorReporter } from "./errorReporter";

const BASE_URL = import.meta.env.VITE_API_URL ?? "";

export class ApiError extends Error {
  readonly status: number | undefined;
  readonly path: string;
  /** `detail` field of a problem-json error body, when present. */
  readonly detail: string | undefined;

  constructor(message: string, path: string, status?: number, detail?: string) {
    super(message);
    this.name = "ApiError";
    this.path = path;
    this.status = status;
    this.detail = detail;
  }
}

/**
 * Wallet sessions carry `address`, demo sessions `email`. Every field is
 * optional so a drifted shape degrades; `auth.ts` normalises it.
 */
export interface AuthUser {
  kind?: string;
  address?: string;
  email?: string;
  /** Wallet sessions: whether the bound email passed the venue allowlist. */
  emailVerified?: boolean;
  name?: string;
  initials?: string;
  [key: string]: unknown;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: AuthUser;
}

export interface MeResponse {
  user: AuthUser;
}

/** `message` is the ready-to-sign EIP-4361 text. */
export interface NonceResponse {
  nonce?: string;
  message?: string;
  [key: string]: unknown;
}

export interface WalletLoginRequest {
  address: string;
  message: string;
  signature: string;
  /**
   * The email gate's Supabase token; the backend verifies it and records the
   * email and allowlist decision.
   */
  supabaseToken?: string;
}

export interface WalletLoginResponse {
  token: string;
  user: AuthUser;
}

/**
 * The deployment descriptor. Every field is optional and every address
 * nullable: callers degrade (`runtimeConfig.ts`) and never default an address.
 */
export interface ConfigTokenResponse {
  symbol?: string;
  name?: string;
  decimals?: number;
  address?: string | null;
  kind?: string;
  [key: string]: unknown;
}

export interface ConfigResponse {
  chainId?: number;
  network?: string;
  contracts?: {
    lop?: string | null;
    settlement?: string | null;
    [key: string]: unknown;
  };
  tokens?: ConfigTokenResponse[];
  bookTokens?: ConfigTokenResponse[];
  markets?: { tokens: ConfigTokenResponse[]; directions: { sellToken: string; buyToken: string }[] };
  explorerBaseUrl?: string;
  faucet?: { enabled?: boolean; [key: string]: unknown };
  auth?: { domain?: string; uri?: string; [key: string]: unknown };
  /**
   * When `enabled`, order intake needs a Supabase-verified, allowlisted email
   * bound at wallet login. The anon key is public by design: Supabase RLS
   * protects the tables behind it.
   */
  emailGate?: {
    enabled?: boolean;
    supabaseUrl?: string | null;
    supabaseAnonKey?: string | null;
    [key: string]: unknown;
  };
  /**
   * `projectId` is public by design: Reown's domain allowlist binds it to this
   * site. Absent or empty: injected wallets only.
   */
  walletConnect?: { projectId?: string | null; [key: string]: unknown };
  /** Venue limits the SPA enforces before asking for a signature. */
  limits?: { maxTicketUsd?: number; [key: string]: unknown };
  [key: string]: unknown;
}

export interface HealthStatus {
  status?: string;
  [key: string]: unknown;
}

export interface AuctionInfo {
  startRateBumpBps?: number;
  durationSec?: number;
  startTime?: string;
  endTime?: string;
  startRate?: number;
  endRate?: number;
  durationMs?: number;
  [key: string]: unknown;
}

/**
 * Which leg the user fixed. `exact-in` = "spend exactly this much",
 * `exact-out` = "give me exactly this much".
 */
export type QuoteSide = "exact-in" | "exact-out";

export interface TypedDataField {
  name: string;
  type: string;
}

/** Domain and types only: the backend does not know the maker, so the SPA supplies `message`. */
export interface QuoteTypedData {
  domain?: Record<string, unknown>;
  types?: Record<string, unknown>;
  primaryType?: string;
  [key: string]: unknown;
}

/**
 * The drafted LOP v4 order. `maker` comes back null and is the only field the
 * SPA may fill: `salt`'s low 160 bits are `keccak256(extension)`, so editing
 * any other field makes the order unfillable on chain.
 */
export interface QuoteDraftOrder {
  salt?: string;
  maker?: string | null;
  receiver?: string;
  makerAsset?: string;
  takerAsset?: string;
  makingAmount?: string;
  takingAmount?: string;
  makerTraits?: string;
  [key: string]: unknown;
}

export interface Quote {
  rate: number;
  /** Base units of the buy leg, in the buy token's decimals. */
  buyAmount: string;
  /**
   * Base units of the sell leg. Optional, so callers refuse to build an order
   * without the leg they need.
   */
  sellAmount?: string;
  /** Which leg the backend treated as fixed. */
  side?: QuoteSide;
  /**
   * The tolerance the quote was priced with. With none echoed, the floor is the
   * backend's default, and the UI must not claim the user's tolerance applied.
   */
  slippageBps?: number;
  auction: AuctionInfo;
  /** Unix seconds, or an ISO string from an older gateway. */
  expiresAt: number | string;

  /* The signable draft, present only when the deployment can build one. Read it
   * through `parseQuoteDraft()`, which returns null unless all of it is usable. */

  order?: QuoteDraftOrder;
  /** Signed through the salt. */
  extension?: string;
  /** Informational: the extension already wires it. */
  settlement?: string;
  typedData?: QuoteTypedData;
  /** Why no draft was produced. Present only when the gateway tried and failed. */
  draftUnavailable?: string;
}

/** A quote draft validated field by field: everything to sign and submit but the maker. */
export interface SignableDraft {
  /** Verbatim from the draft. */
  order: Omit<LopOrder, "maker">;
  /** Submitted unchanged: the salt commits to it. */
  extension: string;
  domain: Record<string, unknown>;
  /** The `Order` struct's fields, as the backend spells them. */
  orderType: TypedDataField[];
  primaryType: string;
}

const DECIMAL_RE = /^\d+$/;
const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/;
const HEX_RE = /^0x[0-9a-fA-F]*$/;

/** A uint256 wire field as a decimal string, or null. Numbers are tolerated. */
function decimalField(value: unknown): string | null {
  if (typeof value === "number") {
    return Number.isInteger(value) && value >= 0 ? String(value) : null;
  }
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return DECIMAL_RE.test(trimmed) ? trimmed : null;
}

function addressField(value: unknown): string | null {
  return typeof value === "string" && ADDRESS_RE.test(value) ? value : null;
}

/** `[{name,type},…]`, or null when any entry is not a field descriptor. */
function typeFields(value: unknown): TypedDataField[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const fields: TypedDataField[] = [];
  for (const raw of value as unknown[]) {
    if (raw === null || typeof raw !== "object") return null;
    const { name, type } = raw as { name?: unknown; type?: unknown };
    if (typeof name !== "string" || typeof type !== "string") return null;
    fields.push({ name, type });
  }
  return fields;
}

/**
 * The quote's signable draft, validated, or null when it has none or its shape
 * drifted. Never patch a half-parsed draft: it is signable only in one piece.
 */
export function parseQuoteDraft(quote: Quote): SignableDraft | null {
  const raw = quote.order;
  if (raw === undefined || raw === null || typeof raw !== "object") return null;
  if (typeof quote.extension !== "string" || !HEX_RE.test(quote.extension)) return null;

  const salt = decimalField(raw.salt);
  const makingAmount = decimalField(raw.makingAmount);
  const takingAmount = decimalField(raw.takingAmount);
  const makerTraits = decimalField(raw.makerTraits);
  const receiver = addressField(raw.receiver);
  const makerAsset = addressField(raw.makerAsset);
  const takerAsset = addressField(raw.takerAsset);
  if (
    salt === null ||
    makingAmount === null ||
    takingAmount === null ||
    makerTraits === null ||
    receiver === null ||
    makerAsset === null ||
    takerAsset === null
  ) {
    return null;
  }

  const domain = quote.typedData?.domain;
  if (domain === undefined || domain === null || typeof domain !== "object") return null;
  if (Array.isArray(domain)) return null;
  const types = quote.typedData?.types;
  const orderType = typeFields(
    types === undefined || types === null ? undefined : (types as Record<string, unknown>).Order,
  );
  if (orderType === null) return null;
  const primaryType = quote.typedData?.primaryType;

  return {
    order: { salt, receiver, makerAsset, takerAsset, makingAmount, takingAmount, makerTraits },
    extension: quote.extension,
    domain: domain as Record<string, unknown>,
    orderType,
    primaryType: typeof primaryType === "string" && primaryType !== "" ? primaryType : "Order",
  };
}

interface QuoteParamsBase {
  sellToken: string;
  buyToken: string;
  /**
   * Not a display setting: the backend folds it into the signed floor
   * (`takingAmount`). Omitted: the backend's default.
   */
  slippageBps?: number;
}

/**
 * The quote takes exactly one of the two legs; the `?: never` arms make passing
 * both a compile error.
 */
export interface ExactInQuoteParams extends QuoteParamsBase {
  /** Base units of the sell token — the leg the user fixed. */
  sellAmount: string;
  buyAmount?: never;
}

export interface ExactOutQuoteParams extends QuoteParamsBase {
  /** Base units of the buy token — the leg the user fixed. */
  buyAmount: string;
  sellAmount?: never;
}

export type QuoteParams = ExactInQuoteParams | ExactOutQuoteParams;

/** Human-readable order view the gateway derives server-side (address→symbol). */
export interface IntentOrder {
  sellToken: string;
  buyToken: string;
  sellAmount: string;
  buyAmount: string;
}

/** On-wire order shape — the LOP v4 8-field struct, amounts as decimal strings. */
export interface LopOrder {
  salt: string;
  maker: string;
  receiver: string;
  makerAsset: string;
  takerAsset: string;
  makingAmount: string;
  takingAmount: string;
  makerTraits: string;
}

/** 65 zero bytes — accepted by the gateway as an unsigned demo intent. */
export const DEMO_SIGNATURE = `0x${"00".repeat(65)}`;

/**
 * The placeholder maker of demo (non-wallet) orders, which nobody controls; the
 * backend marks them unverified. Orders filters on it to show the demo book.
 */
export const DEMO_MAKER = "0xde3070d1a1a1de3070d1a1a1de3070d1a1a1de30";

export interface SubmitOrderRequest {
  order: LopOrder;
  signature: string;
  extension?: string;
}

/**
 * An indexed on-chain event of an order: a fill or a cancellation. Every field
 * is optional (a partly indexed record may lack a decoded amount); without a
 * `txHash`, callers show no explorer link.
 */
export interface OrderFill {
  txHash?: string;
  logIndex?: number;
  blockNumber?: number;
  blockTimestamp?: string;
  /** "fill" | "cancel" — spelled loosely so an unknown kind still renders. */
  kind?: string;
  /** Base units of the maker (sell) asset that this event settled. */
  filledMakingAmount?: string;
  /**
   * Base units of the buy asset the maker received: the only honest executed
   * price, since an auction fill pays above the signed floor. `OrderFilled`
   * carries none, so without it show no price, never the floor.
   */
  filledTakingAmount?: string;
  taker?: string;
  [key: string]: unknown;
}

export interface OrderRecord {
  orderHash: string;
  order: LopOrder;
  /** Server-derived symbols/amounts; present on list/get responses. */
  display?: IntentOrder;
  status: string;
  createdAt: string;
  /**
   * Making amount still unfilled, in base units, updated from every
   * `OrderFilled`. Without it the UI shows unknown progress, not an untouched order.
   */
  remainingMakingAmount?: string;
  /** True when the backend verified the EIP-712 signature against the maker. */
  verified?: boolean;
  /** Newest first. */
  fills?: OrderFill[];
  /**
   * A filler's broadcast, not yet mined. A hint that expires by itself and goes
   * when a fill is indexed: shown as "being filled", never as a trade.
   */
  pendingFill?: { txHash: string; filler?: string; claimedAt?: string } | null;
}

export interface FaucetStatus {
  enabled?: boolean;
  /** symbol -> token contract address */
  tokens?: Record<string, string>;
  amountPerToken?: string;
  cooldownSeconds?: number;
  [key: string]: unknown;
}

export interface FaucetDrip {
  token: string;
  txHash?: string;
  error?: string;
}

export interface FaucetResponse {
  address?: string;
  drips?: FaucetDrip[];
  [key: string]: unknown;
}

interface RequestOptions {
  cache?: RequestCache;
  method?: "GET" | "POST" | "DELETE";
  body?: unknown;
  /** Skip error reporting (used by the health poller — a red dot is the signal). */
  silent?: boolean;
  /** Statuses the caller handles: they still throw `ApiError`, but are not reported. */
  expectedStatuses?: readonly number[];
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  // Paths carry the default "/api" prefix; the active chain's backend is under
  // its own ("/api/base").
  const url = `${BASE_URL}${path.replace(/^\/api\//, `${getApiBase()}/`)}`;
  const init: RequestInit = { method: options.method ?? "GET", ...(options.cache ? { cache: options.cache } : {}) };
  const headers: Record<string, string> = {};
  const token = getStoredToken();
  if (token !== null) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(options.body);
  }
  if (Object.keys(headers).length > 0) {
    init.headers = headers;
  }

  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (err) {
    const apiError = new ApiError(
      `Network error calling ${path}: ${err instanceof Error ? err.message : String(err)}`,
      path,
    );
    if (!options.silent) {
      errorReporter.capture(apiError.message, {
        level: "error",
        meta: { source: "api", path },
      });
    }
    throw apiError;
  }

  if (!res.ok) {
    let bodyText = "";
    try {
      bodyText = (await res.text()).slice(0, 300);
    } catch {
      // Unreadable body: the status alone is enough.
    }
    let problemDetail: string | undefined;
    try {
      const parsed: unknown = JSON.parse(bodyText);
      if (
        parsed !== null &&
        typeof parsed === "object" &&
        typeof (parsed as { detail?: unknown }).detail === "string"
      ) {
        problemDetail = (parsed as { detail: string }).detail;
      }
    } catch {
      // Not JSON: no detail.
    }
    const apiError = new ApiError(
      `API ${path} failed with ${res.status}${bodyText ? `: ${bodyText}` : ""}`,
      path,
      res.status,
      problemDetail,
    );
    const expected = options.expectedStatuses?.includes(res.status) ?? false;
    if (!options.silent && !expected) {
      errorReporter.capture(apiError.message, {
        level: "error",
        meta: { source: "api", path, status: res.status },
      });
    }
    throw apiError;
  }

  return (await res.json()) as T;
}

/** The Supabase session the email gate stores (wire shape, untrusted). */
export interface SupabaseSessionResponse {
  access_token?: string;
  refresh_token?: string;
  /** Unix seconds. */
  expires_at?: number;
  expires_in?: number;
  user?: { email?: string; [key: string]: unknown };
  [key: string]: unknown;
}

/**
 * The email gate's calls to Supabase, an external origin, so not through
 * `request()`. Nothing is reported: callers handle 429 (rate limit) and 403
 * (wrong or expired code) as states.
 */
async function supabaseAuth<T>(
  supabaseUrl: string,
  anonKey: string,
  path: string,
  body: object,
  accessToken?: string,
): Promise<T> {
  const url = `${supabaseUrl.replace(/\/+$/, "")}${path}`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: {
        apikey: anonKey,
        "Content-Type": "application/json",
        ...(accessToken !== undefined ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new ApiError(
      `Supabase auth request failed: ${err instanceof Error ? err.message : String(err)}`,
      path,
    );
  }
  const text = await res.text();
  if (!res.ok) {
    let detail: string | undefined;
    try {
      const parsed = JSON.parse(text) as { msg?: unknown; error_description?: unknown };
      detail =
        typeof parsed.msg === "string"
          ? parsed.msg
          : typeof parsed.error_description === "string"
            ? parsed.error_description
            : undefined;
    } catch {
      // Non-JSON error body — the status alone has to do.
    }
    throw new ApiError(`Supabase auth ${path} returned ${res.status}`, path, res.status, detail);
  }
  return (text === "" ? {} : JSON.parse(text)) as T;
}

export const api = {
  health(): Promise<HealthStatus> {
    // Under /api so the active chain's prefix applies: the dot reports the
    // backend the page trades on.
    return request<HealthStatus>("/api/health", { silent: true });
  },

  /** On 404 the SPA knows no contract address and refuses to sign. */
  config(): Promise<ConfigResponse> {
    return request<ConfigResponse>("/api/v1/config", { expectedStatuses: [404], cache: "no-store" });
  },

  /** 401 means bad credentials — an expected, handled state. */
  login(body: LoginRequest): Promise<LoginResponse> {
    return request<LoginResponse>("/api/v1/auth/login", {
      method: "POST",
      body,
      expectedStatuses: [401],
    });
  },

  /** SIWE challenge. 400 is a bad address, 404 a backend without wallet sign-in. */
  authNonce(address: string): Promise<NonceResponse> {
    const qs = new URLSearchParams({ address });
    return request<NonceResponse>(`/api/v1/auth/nonce?${qs.toString()}`, {
      expectedStatuses: [400, 404],
    });
  },

  /** 401 means the signature did not verify — an expected, handled state. */
  walletLogin(body: WalletLoginRequest): Promise<WalletLoginResponse> {
    return request<WalletLoginResponse>("/api/v1/auth/wallet", {
      method: "POST",
      body,
      expectedStatuses: [401, 404],
    });
  },

  /**
   * 401 is a dead token; the boot check passes `silent` so a flaky backend cannot
   * flood the reporter.
   */
  me(opts: { silent?: boolean } = {}): Promise<MeResponse> {
    return request<MeResponse>("/api/v1/auth/me", {
      expectedStatuses: [401],
      ...(opts.silent ? { silent: true } : {}),
    });
  },

  /** Email gate: ask Supabase to mail a 6-digit code. 429 = rate limited. */
  emailOtpSend(supabaseUrl: string, anonKey: string, email: string): Promise<void> {
    return supabaseAuth<void>(supabaseUrl, anonKey, "/auth/v1/otp", {
      email,
      create_user: true,
    });
  },

  /** Email gate: exchange the mailed code for a Supabase session. 403 = wrong/expired code. */
  emailOtpVerify(
    supabaseUrl: string,
    anonKey: string,
    email: string,
    code: string,
  ): Promise<SupabaseSessionResponse> {
    return supabaseAuth<SupabaseSessionResponse>(supabaseUrl, anonKey, "/auth/v1/verify", {
      type: "email",
      email,
      token: code,
    });
  },

  /**
   * Email gate: "approved", "already" or "waitlisted" (a first-timer is
   * waitlisted). The RPC reads the email from the caller's own JWT, so it asks
   * only about the caller; UX only, as the backend checks again at login.
   */
  emailAccessCheck(supabaseUrl: string, anonKey: string, accessToken: string): Promise<string> {
    return supabaseAuth<string>(supabaseUrl, anonKey, "/rest/v1/rpc/check_access", {}, accessToken);
  },

  /** Email gate: refresh an expired Supabase access token. */
  emailOtpRefresh(
    supabaseUrl: string,
    anonKey: string,
    refreshToken: string,
  ): Promise<SupabaseSessionResponse> {
    return supabaseAuth<SupabaseSessionResponse>(
      supabaseUrl,
      anonKey,
      "/auth/v1/token?grant_type=refresh_token",
      { refresh_token: refreshToken },
    );
  },

  /** The guard catches a widened value at run time: a reported client bug beats an opaque 400. */
  getQuote(params: QuoteParams): Promise<Quote> {
    const path = "/api/v1/intent-quote";
    const { sellAmount, buyAmount } = params;
    if ((sellAmount === undefined) === (buyAmount === undefined)) {
      const message = `API ${path} needs exactly one of sellAmount/buyAmount`;
      errorReporter.capture(message, { level: "error", meta: { source: "api", path } });
      return Promise.reject(new ApiError(message, path));
    }
    const qs = new URLSearchParams({
      sellToken: params.sellToken,
      buyToken: params.buyToken,
    });
    if (sellAmount !== undefined) qs.set("sellAmount", sellAmount);
    if (buyAmount !== undefined) qs.set("buyAmount", buyAmount);
    if (params.slippageBps !== undefined) qs.set("slippageBps", String(params.slippageBps));
    return request<Quote>(`${path}?${qs.toString()}`);
  },

  submitOrder(body: SubmitOrderRequest): Promise<OrderRecord> {
    return request<OrderRecord>("/api/v1/orders", { method: "POST", body });
  },

  /**
   * `maker` filters on the server: filtering here would show other makers'
   * orders and truncate at `limit`. `silent` is for the auto-refresh.
   */
  async listOrders(
    opts: { limit: number; maker?: string; silent?: boolean } | number,
  ): Promise<OrderRecord[]> {
    const { limit, maker, silent } = typeof opts === "number" ? { limit: opts } : opts;
    const qs = new URLSearchParams({ limit: String(limit) });
    if (maker !== undefined && maker !== "") qs.set("maker", maker);
    // A bare array is tolerated so a shape change degrades instead of blanking the page.
    const res = await request<{ orders?: OrderRecord[] } | OrderRecord[]>(
      `/api/v1/orders?${qs.toString()}`,
      silent === true ? { silent: true } : {},
    );
    if (Array.isArray(res)) return res;
    return Array.isArray(res.orders) ? res.orders : [];
  },

  /**
   * Always silent: its one caller, the outcome poller, asks every few seconds,
   * and a blip there is no fault. 404 means the backend forgot the order.
   */
  getOrder(orderHash: string): Promise<OrderRecord> {
    return request<OrderRecord>(`/api/v1/orders/${orderHash}`, {
      silent: true,
      expectedStatuses: [404],
    });
  },

  /**
   * Removes the order from the backend's book. Not a cancellation: the
   * signature stays valid, and a solver holding the order can still fill it.
   * Call it only once `sendCancelOrder` is mined (`cancelIntent.ts`).
   * 404 (gone) and 409 (terminal) are handled states.
   */
  deleteOrder(orderHash: string): Promise<OrderRecord> {
    return request<OrderRecord>(`/api/v1/orders/${orderHash}`, {
      method: "DELETE",
      expectedStatuses: [404, 409],
    });
  },

  /** 404/503 mean "faucet not available on this deployment" — expected. */
  faucetStatus(): Promise<FaucetStatus> {
    return request<FaucetStatus>("/api/v1/faucet/status", {
      expectedStatuses: [404, 503],
    });
  },

  /** 400 invalid address, 429 cooldown, 503 disabled — all expected states. */
  requestFaucet(address: string): Promise<FaucetResponse> {
    return request<FaucetResponse>("/api/v1/faucet", {
      method: "POST",
      body: { address },
      expectedStatuses: [400, 404, 429, 503],
    });
  },
};
